import { atom, useAtom } from 'jotai'
import qs from 'query-string'
import { useToastState } from '~/atoms/ToastState'
import { useUserState } from '~/atoms/UserState'
import { usePopupWindow } from '~/hooks/useElectronWindow'
import { useEmitterService } from '~/hooks/useEmitterService'
import { useWebSocketService } from '~/hooks/useWebSocketService'
import { MessagingToastStatus } from '~/pages/Messaging/MessagingToast'
import {
  GetMessageThreadsParams,
  useCreateThread,
  useGetMessageThread,
  useGetMessageThreads,
  useMarkMessageThreadAsRead,
  useUpdateThreadName,
} from '~/queries/messagingQueries'
import { MessagingEmitterEventType } from '~/services/EventEmitter/types/EmitterTypeDefs'
import Logger from '~/services/Logger'
import { WebSocketEvent, WebSocketEventType } from '~/types/WebSocketTypes'

import {
  MessageThread,
  MessageThreadGroups,
  PaginatedApiResult,
} from '@mangovoice/types'

const MESSAGE_THREADS_CACHE_TIME = 1000 * 60 * 5 // 5 minutes

interface MessageThreadsState {
  messageThreadsByGroup: MessageThreadGroups
  nextThreadPage?: string | null
  selectedThread?: MessageThread
  searchFilter?: string
  unreadsFilter?: boolean
  groupId?: number
}

const messageThreadsAtom = atom<MessageThreadsState>({
  messageThreadsByGroup: new Map<number, MessageThread[]>(),
  groupId: 0,
})

const logger = new Logger('MessageThreadsViewModelLogger')
export const useMessageThreadsViewModel = () => {
  const { addTextToPopupQueue } = usePopupWindow()
  const { pbxId } = useUserState()
  const [messageThreadsState, setMessageThreadsState] =
    useAtom(messageThreadsAtom)
  const { subscribe: wssSubscribe } = useWebSocketService()

  const { subscribe: emitterSubscribe, emit } = useEmitterService()

  const getMessageThreadsByGroup = (
    messageThreadGroups: MessageThreadGroups,
  ): MessageThread[] => {
    if (messageThreadsState.groupId) {
      return messageThreadGroups.get(messageThreadsState.groupId) ?? []
    }
    return []
  }

  const handleUpdateMessageThreadState = (
    newState:
      | Partial<MessageThreadsState>
      | ((previousState: MessageThreadsState) => Partial<MessageThreadsState>),
  ) => {
    setMessageThreadsState((prevState) => {
      const resolvedNewState =
        typeof newState === 'function' ? newState(prevState) : newState

      const updatedState = {
        ...prevState,
        ...resolvedNewState,
      }

      // Check if messageThreadsByGroup is being updated and a thread is selected
      if (resolvedNewState.messageThreadsByGroup && prevState.selectedThread) {
        const groupId = updatedState.groupId || 0
        const updatedGroup =
          updatedState.messageThreadsByGroup.get(groupId) || []
        const refreshedSelectedThread = updatedGroup.find(
          (t) => t.id === prevState.selectedThread?.id,
        )

        if (refreshedSelectedThread) {
          // Update selectedThread to the refreshed thread from the group
          updatedState.selectedThread = refreshedSelectedThread
        } else {
          // If the selected thread no longer exists in the group, deselect it
          updatedState.selectedThread = undefined
        }
      }

      return updatedState
    })
  }

  const setSelectedThread = (thread?: MessageThread) => {
    handleUpdateMessageThreadState({
      selectedThread: thread ? { ...thread } : undefined,
    })
  }

  const updateThreadGroupMap = (data: MessageThread[]) => {
    const resultGroups: MessageThreadGroups = new Map(
      messageThreadsState.messageThreadsByGroup,
    )
    if (!resultGroups.get(0)) {
      resultGroups.set(0, [])
    }

    for (const thread of data) {
      const threadGroupId = thread.message_group_id
      if (!resultGroups.has(threadGroupId)) {
        resultGroups.set(threadGroupId, [])
      }

      const group = resultGroups.get(threadGroupId)!
      const existingThreadIndex = group.findIndex((t) => t.id === thread.id)
      if (existingThreadIndex !== -1) {
        group[existingThreadIndex] = thread
      } else {
        group.push(thread)
      }

      //update the all threads group
      const allThreadsGroup = resultGroups.get(0)!
      const existingAllThreadsThreadIndex = allThreadsGroup.findIndex(
        (t) => t.id === thread.id,
      )
      if (existingAllThreadsThreadIndex !== -1) {
        allThreadsGroup[existingAllThreadsThreadIndex] = thread
      } else {
        allThreadsGroup.push(thread)
      }
    }

    resultGroups.forEach((group) => {
      //sort each group by id descending
      group.sort((a, b) => b.id - a.id)
    })

    return resultGroups
  }

  const handleOnQuerySuccess = (data: PaginatedApiResult<MessageThread>) => {
    //remove empty threads from the list - issue a Sentry warning if this occurs
    const emptyThreads = data.results.filter((t) => !t.latest_message)
    if (emptyThreads.length > 0) {
      logger.warn(
        `Found ${emptyThreads.length} empty threads in group: ${messageThreadsState.groupId} for PBX: ${pbxId}`,
        {
          threadIds: emptyThreads.map((t) => t.id),
        },
      )
    }

    const nonEmptyThreads = data.results.filter((t) => t.latest_message)
    const merged = updateThreadGroupMap(nonEmptyThreads)

    handleUpdateMessageThreadState((prev) => {
      return {
        ...prev,
        messageThreadsByGroup: merged,
        nextThreadPage: data.next,
      }
    })
  }

  const getPaginationParamsFromQueryString = (
    queryString: string,
  ): GetMessageThreadsParams => {
    const params = qs.parse(queryString)
    return {
      groupId: params.group_id ? Number(params.group_id) : undefined,
      offset: params.offset ? Number(params.offset) : 0,
      search: params.search ? String(params.search) : undefined,
      hasUnreadMessages: params.has_unread_messages
        ? Boolean(params.has_unread_messages)
        : undefined,
    }
  }

  const resetState = () => {
    setMessageThreadsState({
      messageThreadsByGroup: new Map<number, MessageThread[]>(),
    })
  }

  const {
    refetch: getMessageThreadsInternal,
    isFetching: messageThreadsLoading,
    isError: messageThreadsLoadErrored,
  } = useGetMessageThreads({
    pbxId,
    onQuerySuccess: handleOnQuerySuccess,
    options: {
      enabled: false,
      refetchOnWindowFocus: true,
      gcTime: MESSAGE_THREADS_CACHE_TIME,
    },
  })

  const getMessageThreads = () => {
    getMessageThreadsInternal({
      search: messageThreadsState.searchFilter,
      hasUnreadMessages: messageThreadsState.unreadsFilter,
      groupId: messageThreadsState.groupId,
    })
  }

  const tryGetNextThreadPage = () => {
    if (messageThreadsLoading) {
      return
    }

    const next = messageThreadsState.nextThreadPage
    if (!next) {
      return
    }
    const params = getPaginationParamsFromQueryString(next)
    getMessageThreadsInternal(params)
  }

  const setLocalThreadUnreadCount = (threadId: number, count: number) => {
    const newThreadGroups = new Map<number, MessageThread[]>(
      messageThreadsState.messageThreadsByGroup,
    )

    //find the thread in the current group in the map
    const thread = getMessageThreadsByGroup(newThreadGroups).find(
      (t) => t.id === threadId,
    )

    if (!thread) {
      logger.debug(
        'Could not find thread in group map - this should not happen ever',
      )
      return
    }

    thread.unread_message_count = count
    handleUpdateMessageThreadState({
      ...messageThreadsState,
      messageThreadsByGroup: newThreadGroups,
    })
  }

  const { mutate: markMessageThreadReadInternal } = useMarkMessageThreadAsRead({
    onSuccess: ({ threadId }) => {
      setLocalThreadUnreadCount(threadId, 0)
      emit(MessagingEmitterEventType.MESSAGE_THREAD_MARKED_READ, {
        threadId,
      })
    },
  })

  const updateThreadState = (thread: MessageThread) => {
    const threadsByGroup = getMessageThreadsByGroup(
      messageThreadsState.messageThreadsByGroup,
    )

    const existingThreadIndex = threadsByGroup.findIndex(
      (t) => t.id === thread.id,
    )

    if (existingThreadIndex !== -1) {
      //update the thread
      threadsByGroup[existingThreadIndex] = thread
    } else {
      //add the thread
      threadsByGroup.push(thread)
    }

    addTextToPopupQueue(
      threadsByGroup.filter(
        (g: MessageThread) =>
          g.unread_message_count > 0 &&
          g.latest_message?.direction === 'inbound',
      ),
    )

    const merged = updateThreadGroupMap(threadsByGroup)
    handleUpdateMessageThreadState({
      ...messageThreadsState,
      messageThreadsByGroup: merged,
    })
  }

  const {
    messagingToast: [, setMessagingToastState],
  } = useToastState()
  const { mutate: tryCreateThreadInternal } = useCreateThread({
    onSuccess: (data) => {
      //locally add the new thread to the thread list
      const threadResult = data.threadResult

      const thread = {
        id: threadResult.id,
        message_group_id: threadResult.group_id,
        unread_message_count: 0,
        external_phone_number: threadResult.external_phone_number,
        latest_message: data.messageResult?.message,
        created_at: threadResult.created_at,
        updated_at: threadResult.updated_at,
        name: threadResult.name,
      }

      updateThreadState(thread)

      emit(MessagingEmitterEventType.MESSAGE_THREAD_CREATED, {
        thread,
        message: data.messageResult?.message,
      })
    },
    onError: (_) => {
      setMessagingToastState({
        messagingStatus: MessagingToastStatus.ErrorSendMessage,
        messagingError:
          'Oops!  An error occurred while sending this message - please try again.',
      })
    },
  })

  /** Marks a message thread as read */
  const markMessageThreadRead = (messageThread: MessageThread) => {
    if (messageThread.unread_message_count === 0) {
      return
    }

    markMessageThreadReadInternal(messageThread.id)
  }

  const { mutate: renameThread } = useUpdateThreadName({
    onSuccess: (data) => {
      //thread name was updated
      updateThreadState(data.response)

      emit(MessagingEmitterEventType.MESSAGE_THREAD_UPDATED, {
        thread: data.response,
      })
    },
  })

  const setFilters = (
    searchFilter?: string,
    unreadsFilter?: boolean,
    groupId?: number,
    requery = true,
  ) => {
    //when filter changes occur, blow away the current thread list and start over
    handleUpdateMessageThreadState({
      ...messageThreadsState,
      messageThreadsByGroup: new Map<number, MessageThread[]>(),
      nextThreadPage: undefined,
      searchFilter,
      unreadsFilter,
      groupId,
    })

    if (!requery) {
      return
    }

    getMessageThreadsInternal({
      search: searchFilter,
      hasUnreadMessages: unreadsFilter,
      groupId,
    })
  }

  emitterSubscribe({
    subscriptionToken: 'MessageThreadsViewModel:MessageGroupMarkedRead',
    subscription: {
      eventType: MessagingEmitterEventType.MESSAGE_GROUPS_MARKED_READ,
      callback: (_) => {
        logger.debug(
          'Received EmitterEventType.MESSAGE_GROUPS_MARKED_READ - performing local update on thread unread counts',
        )

        //this can be a UI-only update and does not require a server refresh of source data, since this
        //event signifies that the user has marked all groups read.
        const newMessageThreadsByGroup = new Map<number, MessageThread[]>(
          messageThreadsState.messageThreadsByGroup,
        )
        newMessageThreadsByGroup.forEach((group) => {
          group.forEach((thread) => {
            if (thread.unread_message_count > 0) {
              thread.unread_message_count = 0
            }
          })
        })

        handleUpdateMessageThreadState({
          ...messageThreadsState,
          messageThreadsByGroup: newMessageThreadsByGroup,
        })
      },
    },
  })

  const wssFilter = (
    event: WebSocketEvent<WebSocketEventType.MessageReceived>,
  ) => {
    return event.payload.threadId === messageThreadsState.selectedThread?.id
  }

  const onMessageReceivedCallback = () => {
    if (!messageThreadsState.selectedThread) {
      logger.debug('No selected thread - not marking thread as read')
      return
    }

    markMessageThreadReadInternal(messageThreadsState.selectedThread.id)
  }

  //when a message is received, if the current selected thread is the thread the message came in on, mark the thread as read
  wssSubscribe(WebSocketEventType.MessageReceived, {
    subscriptionToken: 'MessageThreadsViewModel:MessageReceived',
    filter: wssFilter,
    callback: (_) => onMessageReceivedCallback(),
  })

  const { refetch: getMessageThreadInternal } = useGetMessageThread({
    onQuerySuccess: (data) => {
      updateThreadState(data)
    },
  })

  //when a thread is updated, get the latest thread data and update the local state
  wssSubscribe(WebSocketEventType.ThreadUpdated, {
    subscriptionToken: 'MessageThreadsViewModel:ThreadUpdated',
    callback: (event) => {
      const thread = event.payload
      getMessageThreadInternal(thread.id)
    },
  })

  return {
    getMessageThreads,
    setFilters,
    messageThreadsLoading,
    messageThreadsLoadErrored,
    nextThreadPage: messageThreadsState.nextThreadPage,
    selectedThread: messageThreadsState.selectedThread,
    tryGetNextThreadPage,
    messageThreads: messageThreadsState,
    setSelectedThread,
    markMessageThreadRead,
    renameThread,
    tryGetOrCreateThread: tryCreateThreadInternal,
    resetState,
  }
}
